<!--
CercleDigital InsiderGems, gestion électronique documentaire partagée de l'écosystème Cercle.
Copyright 2026 Pierre-Thibault Fabre <pierre-thibault@cercle.digital>
SPDX-License-Identifier: LicenseRef-Cercle-Interne

Fichier : apps/web/src/shell/PoigneeLaterale.vue
Rôle : redimensionne une colonne latérale au glissement, souris ou doigt.
Référence : designsystem.md ; CIG-SPECIFICATION.md §5.1, §5.5.
-->
<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';

const props = withDefaults(
  defineProps<{
    largeur: number;
    /** En deçà, les libellés seraient tronqués : la colonne ne rétrécit plus. */
    min?: number;
    /** Au-delà, la colonne concurrencerait la zone documentaire. */
    max?: number;
    /**
     * Bord auquel la colonne est ancrée. « gauche » pend la poignée à droite,
     * et le glissement vers la droite élargit ; « droite » en est le miroir
     * exact, poignée à gauche et élargissement vers la gauche.
     */
    cote?: 'gauche' | 'droite';
    /** Ton du plan porteur : la poignée doit s'y détacher. */
    ton?: 'sombre' | 'clair';
    /** Largeur rétablie au double-clic. */
    defaut?: number;
    libelle?: string;
  }>(),
  {
    min: 176,
    max: 448,
    cote: 'gauche',
    ton: 'sombre',
    defaut: 240,
    libelle: 'Largeur de la barre latérale',
  },
);

const emit = defineEmits<{ 'update:largeur': [valeur: number]; termine: [] }>();

/**
 * Sens du geste. Élargir une colonne ancrée à droite suppose de tirer vers la
 * gauche : la poignée suit le doigt, jamais l'inverse.
 */
const sens = computed(() => (props.cote === 'gauche' ? 1 : -1));

const poignee = ref<HTMLElement | null>(null);
const actif = ref(false);
const approche = ref(false);
/**
 * Le doigt n'a pas de survol : sans repère permanent, la poignée resterait
 * introuvable au tactile. Le mode de pointage détermine donc le retour visuel,
 * fin filet révélé à l'approche de la souris, préhension visible au doigt.
 */
const tactile = ref(false);

let depart = 0;
let largeurDepart = 0;

function borner(v: number): number {
  // La colonne ne peut jamais occuper plus de la moitié de la fenêtre, quelle
  // que soit la borne configurée : un écran étroit prime sur le réglage.
  const plafond = Math.min(props.max, Math.round(window.innerWidth / 2));
  return Math.max(props.min, Math.min(plafond, Math.round(v)));
}

function surAbaissement(ev: PointerEvent): void {
  tactile.value = ev.pointerType !== 'mouse';
  actif.value = true;
  depart = ev.clientX;
  largeurDepart = props.largeur;
  poignee.value?.setPointerCapture(ev.pointerId);
  ev.preventDefault();
}

function surDeplacement(ev: PointerEvent): void {
  if (!actif.value) return;
  emit('update:largeur', borner(largeurDepart + sens.value * (ev.clientX - depart)));
  ev.preventDefault();
}

function surRelevement(ev: PointerEvent): void {
  if (!actif.value) return;
  actif.value = false;
  poignee.value?.releasePointerCapture(ev.pointerId);
  emit('termine');
}

/** L'approche du pointeur révèle la poignée, avant même de la saisir. */
function surApproche(ev: PointerEvent): void {
  tactile.value = ev.pointerType !== 'mouse';
  approche.value = true;
}

function surEloignement(): void {
  if (!actif.value) approche.value = false;
}

/**
 * Le redimensionnement doit rester possible sans pointeur : le séparateur est
 * focalisable et se règle aux flèches, par pas confortable. La flèche déplace
 * le séparateur, elle n'ajoute pas de largeur : sur une colonne ancrée à
 * droite, aller à gauche l'élargit donc.
 */
function surTouche(ev: KeyboardEvent): void {
  const pas = (ev.shiftKey ? 48 : 16) * sens.value;
  if (ev.key === 'ArrowLeft') emit('update:largeur', borner(props.largeur - pas));
  else if (ev.key === 'ArrowRight') emit('update:largeur', borner(props.largeur + pas));
  else if (ev.key === 'Home') emit('update:largeur', borner(props.min));
  else if (ev.key === 'End') emit('update:largeur', borner(props.max));
  else return;
  ev.preventDefault();
  emit('termine');
}

/** Un écran rétréci ne doit pas laisser la colonne déborder de la fenêtre. */
function remesurer(): void {
  const borne = borner(props.largeur);
  if (borne !== props.largeur) emit('update:largeur', borne);
}

onMounted(() => {
  tactile.value = window.matchMedia?.('(pointer: coarse)').matches ?? false;
  window.addEventListener('resize', remesurer);
});
onUnmounted(() => window.removeEventListener('resize', remesurer));
</script>

<template>
  <div
    ref="poignee"
    class="poignee"
    :class="[`cote-${cote}`, `ton-${ton}`, { actif, approche, tactile }]"
    role="separator"
    aria-orientation="vertical"
    :aria-label="libelle"
    :aria-valuenow="largeur"
    :aria-valuemin="min"
    :aria-valuemax="max"
    tabindex="0"
    @pointerdown="surAbaissement"
    @pointermove="surDeplacement"
    @pointerup="surRelevement"
    @pointercancel="surRelevement"
    @pointerenter="surApproche"
    @pointerleave="surEloignement"
    @keydown="surTouche"
    @dblclick="emit('update:largeur', borner(defaut))"
  >
    <span class="filet" aria-hidden="true" />
    <span class="prise" aria-hidden="true" />
  </div>
</template>

<style scoped>
/*
 * La zone saisissable déborde largement le filet visible : viser deux pixels
 * à la souris serait pénible, et impossible au doigt.
 */
.poignee {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 13px;
  z-index: 26;
  cursor: col-resize;
  touch-action: none;
  display: grid;
  place-items: center;

  /*
   * Ton de la poignée. Elle se pose aussi bien sur la coque sombre que sur le
   * plan clair du volet de propriétés : seules ces deux teintes changent.
   */
  --poignee-repos: var(--cig-contraste-gris);
  --poignee-actif: var(--cig-contraste-encre);
}
.poignee.ton-clair { --poignee-repos: var(--cig-gris); --poignee-actif: var(--cig-encre); }

/* La poignée pend du côté opposé à l'ancrage de la colonne. */
.poignee.cote-gauche { right: -6px; }
.poignee.cote-droite { left: -6px; }
.poignee.tactile { width: 24px; }
.poignee.cote-gauche.tactile { right: -11px; }
.poignee.cote-droite.tactile { left: -11px; }

.poignee:focus-visible { outline: none; }

/* Filet : quasi invisible au repos, franc dès l'approche ou la saisie. */
.filet {
  position: absolute;
  inset: 0 auto 0 auto;
  width: 2px;
  border-radius: var(--cig-rayon-pilule);
  background: transparent;
  transition: background var(--cig-duree-courte) ease, width var(--cig-duree-courte) ease;
}
.poignee.approche .filet,
.poignee:focus-visible .filet { background: var(--poignee-repos); }
.poignee.actif .filet { background: var(--poignee-actif); width: 3px; }

/*
 * Préhension : au doigt, elle est visible en permanence, faute de survol.
 * À la souris, elle n'apparaît qu'à l'approche.
 */
.prise {
  position: relative;
  width: 4px;
  height: 2.5rem;
  border-radius: var(--cig-rayon-pilule);
  background: var(--poignee-repos);
  opacity: 0;
  transform: scaleY(0.6);
  transition:
    opacity var(--cig-duree-courte) ease,
    transform var(--cig-duree) var(--cig-elan),
    background var(--cig-duree-courte) ease;
}
.poignee.tactile .prise { opacity: 0.75; transform: none; height: 3.25rem; }
.poignee.approche .prise,
.poignee:focus-visible .prise { opacity: 1; transform: none; }
.poignee.actif .prise { background: var(--poignee-actif); opacity: 1; }

@media (prefers-reduced-motion: reduce) {
  .filet, .prise { transition: none; }
}
</style>
